import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { labService } from '../../services'
import {
  BeakerIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayCircleIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid'

export default function LabList() {
  const [labs, setLabs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchLabs()
  }, [])

  const fetchLabs = async () => {
    try {
      setLoading(true)
      const data = await labService.getAll()
      setLabs(data)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load labs')
    } finally {
      setLoading(false)
    }
  }

  const getDifficultyColor = (lab) => {
    // Difficulty based on estimated time
    const minutes = lab.estimated_time_minutes
    if (minutes <= 90) return 'text-green-600 bg-green-50 border-green-200'
    if (minutes <= 120) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-red-600 bg-red-50 border-red-200'
  }

  const getDifficultyText = (lab) => {
    const minutes = lab.estimated_time_minutes
    if (minutes <= 90) return 'Beginner'
    if (minutes <= 120) return 'Intermediate'
    return 'Advanced'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading labs...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 className="text-red-800 font-semibold">Error loading labs</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
        <div className="flex items-center mb-4">
          <BeakerIcon className="h-10 w-10 mr-3" />
          <h1 className="text-4xl font-bold">Practice Labs</h1>
        </div>
        <p className="text-purple-100 text-lg max-w-3xl">
          Hands-on exercises to reinforce your learning. Build real AI assistants and tools with step-by-step guidance.
        </p>
        <div className="mt-6 flex items-center space-x-6">
          <div className="flex items-center">
            <BookOpenIcon className="h-5 w-5 mr-2" />
            <span className="text-purple-100">{labs.length} Labs Available</span>
          </div>
          <div className="flex items-center">
            <CheckCircleSolid className="h-5 w-5 mr-2" />
            <span className="text-purple-100">
              {labs.filter(lab => lab.progress?.status === 'completed').length} Completed
            </span>
          </div>
        </div>
      </div>

      {/* Labs Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {labs.map((lab) => {
          const isCompleted = lab.progress?.status === 'completed'
          const isInProgress = lab.progress?.status === 'in_progress'

          return (
            <Link
              key={lab.id}
              to={`/labs/${lab.id}`}
              className="group bg-white rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-purple-100 rounded-lg p-3 group-hover:bg-purple-200 transition-colors">
                      <BeakerIcon className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="ml-3">
                      <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">
                        {lab.code}
                      </span>
                      <h3 className="text-xl font-bold text-cte-black group-hover:text-purple-600 transition-colors">
                        {lab.title}
                      </h3>
                    </div>
                  </div>
                  {isCompleted && (
                    <CheckCircleSolid className="h-6 w-6 text-green-500 flex-shrink-0" />
                  )}
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {lab.description}
                </p>

                {/* Meta Info */}
                <div className="flex items-center flex-wrap gap-3 mb-4">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${getDifficultyColor(lab)}`}>
                    {getDifficultyText(lab)}
                  </span>
                  <div className="flex items-center text-sm text-gray-600">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {lab.estimated_time_minutes} min
                  </div>
                  {lab.module && (
                    <div className="flex items-center text-sm text-gray-600">
                      <BookOpenIcon className="h-4 w-4 mr-1" />
                      {lab.module.code}
                    </div>
                  )}
                </div>

                {/* Progress Bar */}
                {isInProgress && lab.progress?.completion_percentage > 0 && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>In Progress</span>
                      <span>{Math.round(lab.progress.completion_percentage)}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill bg-purple-500" 
                        style={{ width: `${lab.progress.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Prerequisites */}
                {lab.prerequisites && lab.prerequisites.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-1">Prerequisites:</p>
                    <div className="flex flex-wrap gap-1">
                      {lab.prerequisites.slice(0, 3).map((prereq, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {prereq}
                        </span>
                      ))}
                      {lab.prerequisites.length > 3 && (
                        <span className="text-xs text-gray-500 px-2 py-1">
                          +{lab.prerequisites.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-sm font-semibold text-purple-600 group-hover:text-purple-700 flex items-center">
                    {isCompleted ? (
                      <>
                        <CheckCircleIcon className="h-5 w-5 mr-1" />
                        Review Lab
                      </>
                    ) : isInProgress ? (
                      <>
                        <PlayCircleIcon className="h-5 w-5 mr-1" />
                        Continue Lab
                      </>
                    ) : (
                      <>
                        <PlayCircleIcon className="h-5 w-5 mr-1" />
                        Start Lab
                      </>
                    )}
                  </span>
                  <svg 
                    className="w-5 h-5 text-purple-600 transform group-hover:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {labs.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <BeakerIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Labs Available</h3>
          <p className="text-gray-500">Check back soon for new lab exercises!</p>
        </div>
      )}
    </div>
  )
}
